import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '@/context/AuthContext';
import * as Sentry from '@sentry/react';
import '@/styles/OutstandingCertificatesInputPage.scss';

const EXCLUDED_PROPERTY_IDS = [25, 26];

const OutstandingCertificatesInputPage = () => {
    const { apiFetch } = useAuth();

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [propRes, certRes] = await Promise.all([
                apiFetch('/api/Property/all'),
                apiFetch('/api/Ops/GetOutstandingCertificates'),
            ]);

            const propData = await propRes.json();
            const certData = await certRes.json();

            if (!propData.success) {
                toast.error('Failed to load properties.');
                return;
            }

            const properties = propData.data.filter(p => p.active !== false && !EXCLUDED_PROPERTY_IDS.includes(p.propertyID));

            const certMap = {};
            if (certData.success && Array.isArray(certData.data)) {
                certData.data.forEach(c => { certMap[c.propertyId] = c; });
            }

            const merged = properties
                .sort((a, b) => a.propertyName.localeCompare(b.propertyName))
                .map(p => {
                    const cert = certMap[p.propertyID];
                    return {
                        propertyId: p.propertyID,
                        propertyName: p.propertyName,
                        fire: String(cert ? cert.outstandingFireClearanceCertificateCount : 0),
                        occupancy: String(cert ? cert.outstandingOccupancyCertificateCount : 0),
                    };
                });

            setRows(merged);
        } catch (e) {
            Sentry.captureException(e);
            toast.error('Failed to load data.');
        } finally {
            setLoading(false);
        }
    }, [apiFetch]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleChange = (propertyId, field, value) => {
        // Allow empty string while typing, strip any non-numeric characters
        const cleaned = value.replace(/[^0-9]/g, '');
        setRows(prev =>
            prev.map(r => r.propertyId === propertyId ? { ...r, [field]: cleaned } : r)
        );
    };

    const handleFocus = (propertyId, field) => {
        // Clear the field so the user can type fresh without deleting the 0
        setRows(prev =>
            prev.map(r => r.propertyId === propertyId ? { ...r, [field]: '' } : r)
        );
    };

    const handleBlur = (propertyId, field) => {
        // If left empty, default back to 0
        setRows(prev =>
            prev.map(r => {
                if (r.propertyId !== propertyId) return r;
                return { ...r, [field]: r[field] === '' ? '0' : r[field] };
            })
        );
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = rows.map(r => ({
                propertyId: r.propertyId,
                outstandingFireClearanceCertificateCount: parseInt(r.fire, 10) || 0,
                outstandingOccupancyCertificateCount: parseInt(r.occupancy, 10) || 0,
            }));

            const response = await apiFetch('/api/Ops/UpsertOutstandingCertificates', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            const data = await response.json();

            if (response.ok && data.success) {
                toast.success('Outstanding certificates saved successfully.');
            } else {
                toast.error(data.message || 'Failed to save.');
            }
        } catch (e) {
            Sentry.captureException(e);
            toast.error('An error occurred while saving.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="outstanding-certs-page">
            <div className="page-header">
                <h1>Outstanding Certificates</h1>
            </div>

            {loading ? (
                <div className="state-message">Loading properties...</div>
            ) : rows.length === 0 ? (
                <div className="state-message">No properties found.</div>
            ) : (
                <>
                    <div className="table-wrapper">
                        <table className="certs-table">
                            <thead>
                                <tr>
                                    <th>Property</th>
                                    <th>Outstanding Fire Clearance Certificates</th>
                                    <th>Outstanding Occupancy Certificates</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(row => (
                                    <tr key={row.propertyId}>
                                        <td className="property-name">{row.propertyName}</td>
                                        <td>
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                value={row.fire}
                                                onChange={e => handleChange(row.propertyId, 'fire', e.target.value)}
                                                onFocus={() => handleFocus(row.propertyId, 'fire')}
                                                onBlur={() => handleBlur(row.propertyId, 'fire')}
                                                className="count-input"
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                value={row.occupancy}
                                                onChange={e => handleChange(row.propertyId, 'occupancy', e.target.value)}
                                                onFocus={() => handleFocus(row.propertyId, 'occupancy')}
                                                onBlur={() => handleBlur(row.propertyId, 'occupancy')}
                                                className="count-input"
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="form-actions">
                        <button
                            className="btn-save"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default OutstandingCertificatesInputPage;